import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["rows", "rowTemplate"]
  static values = {
    minDate: String,
    maxDateSpanDays: { type: Number, default: 90 }
  }

  connect() {
    if (this.rowsTarget.children.length === 0) this.addRow()
    this.rowsTarget.querySelectorAll("tr").forEach((row) => this.syncRow(row))
  }

  addRow() {
    const fragment = this.rowTemplateTarget.content.cloneNode(true)
    const row = fragment.firstElementChild

    this.rowsTarget.prepend(fragment)
    this.syncRow(row)
    row.querySelector("[data-employee-select]")?.focus()
  }

  removeRow(event) {
    const row = event.target.closest("tr")

    if (this.rowsTarget.children.length === 1) {
      row.querySelectorAll("input, select").forEach((field) => {
        field.value = ""
        field.disabled = false
      })
      row.querySelector("[data-schedule-note]").textContent = ""
      return
    }

    row.remove()
  }

  employeeChanged(event) {
    this.syncEmployee(event.target.closest("tr"))
  }

  typeChanged(event) {
    this.syncType(event.target.closest("tr"))
  }

  dateChanged(event) {
    const row = event.target.closest("tr")
    this.syncEndDate(row)
    this.validateRow(row)
  }

  validate(event) {
    let firstInvalidField = null

    this.rowsTarget.querySelectorAll("tr").forEach((row) => {
      const invalidField = this.validateRow(row)
      if (!firstInvalidField && invalidField) firstInvalidField = invalidField
    })

    if (firstInvalidField) {
      event.preventDefault()
      firstInvalidField.reportValidity()
    }
  }

  syncRow(row) {
    this.syncEmployee(row)
    this.syncType(row)
    this.syncMinDates(row)
    this.syncEndDate(row)
  }

  syncEmployee(row) {
    const employeeSelect = row.querySelector("[data-employee-select]")
    const selected = employeeSelect.selectedOptions[0]
    const note = row.querySelector("[data-schedule-note]")

    note.textContent = selected?.dataset.schedule || ""
  }

  syncType(row) {
    const typeSelect = row.querySelector("[data-type-select]")
    const selected = typeSelect.selectedOptions[0]
    const requiresSchedule = selected?.dataset.requiresSchedule !== "false"
    const timeFields = row.querySelectorAll("[data-time-field]")

    timeFields.forEach((field) => {
      field.disabled = !requiresSchedule
      field.required = requiresSchedule
      if (!requiresSchedule) field.value = ""
    })

    row.querySelectorAll("[data-end-date-field]").forEach((field) => {
      field.disabled = false
      field.required = true
    })
  }

  syncMinDates(row) {
    row.querySelectorAll("[data-start-date-field], [data-end-date-field]").forEach((field) => {
      field.min = this.minDateValue
    })
  }

  syncEndDate(row) {
    const startDateField = row.querySelector("[data-start-date-field]")
    const endDateField = row.querySelector("[data-end-date-field]")
    if (!startDateField || !endDateField) return

    endDateField.min = startDateField.value || this.minDateValue
    if (startDateField.value && (!endDateField.value || endDateField.value < startDateField.value)) {
      endDateField.value = startDateField.value
    }
    if (startDateField.value) {
      const maxEndDate = this.maxEndDateValue(startDateField.value)
      endDateField.max = maxEndDate
    } else {
      endDateField.removeAttribute("max")
    }
  }

  validateRow(row) {
    const typeSelect = row.querySelector("[data-type-select]")
    const requiresSchedule = typeSelect.selectedOptions[0]?.dataset.requiresSchedule !== "false"

    const startDateField = row.querySelector("[data-start-date-field]")
    const endDateField = row.querySelector("[data-end-date-field]")
    const startTimeField = row.querySelector("[data-start-time-field]")
    const endTimeField = row.querySelector("[data-end-time-field]")

    ;[endDateField, endTimeField].forEach((field) => field?.setCustomValidity(""))

    if (!startDateField?.value || !endDateField?.value) {
      return [startDateField, endDateField].find((field) => field && !field.value)
    }

    if (endDateField.value < startDateField.value) {
      endDateField.setCustomValidity("Η ημερομηνία λήξης πρέπει να είναι ίδια ή μετά την ημερομηνία έναρξης.")
      return endDateField
    }

    if (this.dateSpanDays(startDateField.value, endDateField.value) > this.maxDateSpanDaysValue) {
      endDateField.setCustomValidity(`Το διάστημα δεν μπορεί να ξεπερνά τις ${this.maxDateSpanDaysValue} ημέρες.`)
      return endDateField
    }

    if (!requiresSchedule) return null

    if (!startTimeField?.value || !endTimeField?.value) {
      return [startTimeField, endTimeField].find((field) => field && !field.value)
    }

    if (endTimeField.value === startTimeField.value) {
      endTimeField.setCustomValidity("Η ώρα αποχώρησης δεν μπορεί να είναι ίδια με την ώρα προσέλευσης.")
      return endTimeField
    }

    return null
  }

  maxEndDateValue(startDateValue) {
    const date = this.utcDate(startDateValue)
    date.setUTCDate(date.getUTCDate() + this.maxDateSpanDaysValue - 1)
    return date.toISOString().slice(0, 10)
  }

  dateSpanDays(startDateValue, endDateValue) {
    const millisecondsPerDay = 24 * 60 * 60 * 1000
    return Math.floor((this.utcDate(endDateValue) - this.utcDate(startDateValue)) / millisecondsPerDay) + 1
  }

  utcDate(dateValue) {
    const [year, month, day] = dateValue.split("-").map(Number)
    return new Date(Date.UTC(year, month - 1, day))
  }
}
